import React, { useState, useRef, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Button } from '../Button/Button';
import './DropdownButton.css';

export type ButtonVariant = 'primary' | 'secondary' | 'transparent' | 'link';

export type ButtonSize = 'sm' | 'md' | 'lg';

export interface DropdownButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  children: React.ReactNode; // for button content
  dropdownContent: React.ReactNode; // for dropdown items
  ariaLabel?: string;
  closeOnSelect?: boolean;
  // Portal rendering
  portal?: boolean; // default true
  portalContainer?: HTMLElement | (() => HTMLElement | null);
  portalZIndex?: React.CSSProperties['zIndex'];
  positionStrategy?: 'fixed' | 'absolute';
}

export const DropdownButton = React.memo(function DropdownButton({
  variant = 'primary',
  size = 'md',
  disabled = false,
  children,
  dropdownContent,
  ariaLabel,
  closeOnSelect = true,
  portal = true,
  portalContainer,
  portalZIndex = 'var(--z-index-overlay, 1000)',
  positionStrategy = 'fixed',
}: DropdownButtonProps) {
  const reactId = useId();
  const menuId = `dropdown-menu-${reactId}`;
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<'left' | 'right'>('left');
  const [verticalPosition, setVerticalPosition] = useState<'top' | 'bottom'>(
    'bottom',
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const [portalPos, setPortalPos] = useState<{
    left: number;
    top: number;
    minWidth: number;
  } | null>(null);

  useEffect(() => setMounted(true), []);

  const toggleDropdown = () => {
    if (disabled) return;
    if (!isOpen && portal) {
      const triggerEl = containerRef.current;
      if (triggerEl) {
        const rect = triggerEl.getBoundingClientRect();
        const gap = 0;
        const viewportWidth = window.innerWidth;
        const seedLeft = viewportWidth - rect.right >= rect.left
          ? Math.round(rect.left)
          : Math.round(rect.right - rect.width);
        setPortalPos({
          left: Math.max(0, seedLeft),
          top: Math.round(rect.bottom + gap),
          minWidth: Math.round(rect.width),
        });
      }
    }
    setIsOpen((v) => !v);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideTrigger = containerRef.current?.contains(target) ?? false;
      const insidePortal = menuRef.current?.contains(target as Node) ?? false;
      if (!insideTrigger && !insidePortal) {
        setIsOpen(false);
      }
    };

    const updatePosition = () => {
      const triggerEl = containerRef.current;
      if (!triggerEl) return;
      const rect = triggerEl.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const spaceOnRight = viewportWidth - rect.right;
      const spaceOnLeft = rect.left;
      const alignLeft = spaceOnRight >= spaceOnLeft;
      setMenuPosition(alignLeft ? 'left' : 'right');
      if (!portal || !isOpen) return;
      const gap = 0;
      const viewportMargin = 8;
      const width = Math.round(rect.width);
      // Use offsetWidth/offsetHeight (layout box) instead of
      // getBoundingClientRect() because the menu animates in with
      // `scale: 0.85`, and bounding-rect measurements include CSS
      // transforms. Reading the rect mid-animation would otherwise yield
      // smaller dimensions and cause the menu to jump once the animation
      // settles or the user scrolls.
      const menuEl = menuRef.current;
      const menuWidth = menuEl ? menuEl.offsetWidth || width : width;
      const menuHeight = menuEl ? menuEl.offsetHeight : 0;
      // Pick vertical placement: prefer below; flip up when the menu would
      // overflow the viewport bottom but fits above. When neither side
      // fully fits, pick the side with more room.
      const spaceBelow = viewportHeight - rect.bottom - viewportMargin;
      const spaceAbove = rect.top - viewportMargin;
      let openAbove = false;
      if (menuHeight > 0) {
        if (menuHeight <= spaceBelow) {
          openAbove = false;
        } else if (menuHeight <= spaceAbove) {
          openAbove = true;
        } else {
          openAbove = spaceAbove > spaceBelow;
        }
      }
      setVerticalPosition(openAbove ? 'top' : 'bottom');
      if (positionStrategy === 'fixed') {
        const top = openAbove
          ? Math.round(rect.top - menuHeight - gap)
          : Math.round(rect.bottom + gap);
        if (alignLeft) {
          setPortalPos({
            left: Math.round(rect.left),
            top,
            minWidth: width,
          });
        } else {
          const left = Math.round(rect.right - menuWidth);
          setPortalPos({
            left: Math.max(0, left),
            top,
            minWidth: width,
          });
        }
      } else {
        const containerEl: HTMLElement | null =
          typeof portalContainer === 'function'
            ? (portalContainer() as HTMLElement | null)
            : (portalContainer ?? null);
        const containerRect = containerEl?.getBoundingClientRect();
        const containerLeft = containerRect?.left ?? 0;
        const containerTop = containerRect?.top ?? 0;
        const top = openAbove
          ? Math.round(rect.top - containerTop - menuHeight - gap)
          : Math.round(rect.bottom - containerTop + gap);
        if (alignLeft) {
          setPortalPos({
            left: Math.round(rect.left - containerLeft),
            top,
            minWidth: width,
          });
        } else {
          const left = Math.round(rect.right - containerLeft - menuWidth);
          setPortalPos({
            left: Math.max(0, left),
            top,
            minWidth: width,
          });
        }
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    let resizeObserver: ResizeObserver | null = null;

    if (isOpen) {
      // Defer until menu is in DOM to read size
      requestAnimationFrame(updatePosition);
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      document.addEventListener('mousedown', handleClickOutside, { capture: true });
      document.addEventListener('keydown', onKeyDown);
      // Reposition whenever the menu's intrinsic size changes — e.g. after
      // the open animation settles or when the content (search filter, etc.)
      // changes its dimensions. Without this the right-aligned position can
      // be computed from a transient size and never get corrected until the
      // user triggers a scroll/resize event.
      if (typeof ResizeObserver !== 'undefined' && menuRef.current) {
        resizeObserver = new ResizeObserver(() => updatePosition());
        resizeObserver.observe(menuRef.current);
      }
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('mousedown', handleClickOutside, {
        capture: true,
      } as EventListenerOptions);
      document.removeEventListener('keydown', onKeyDown);
      resizeObserver?.disconnect();
    };
  }, [isOpen, portal, portalContainer, positionStrategy]);

  return (
    <div className="dropdown-button" ref={containerRef} aria-label={ariaLabel}>
      <Button
        variant={variant || 'secondary'}
        size={size}
        disabled={disabled}
        trailingVisual="ChevronDown"
        onClick={toggleDropdown}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
      >
        {children}
      </Button>
      {(() => {
        const menu = (
          <motion.ul
            ref={menuRef}
            id={menuId}
            role="menu"
            className={`dropdown dropdown-button__menu menu--${menuPosition} menu--v-${verticalPosition}`}
            initial={{
              opacity: 0,
              scale: 0.85,
            }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.2, ease: [0.77, 0, 0.175, 1] }}
            // transformOrigin must live in `style` (re-applied on every
            // render) rather than `initial` (only applied at mount).
            // verticalPosition is always 'bottom' on first render and
            // can flip to 'top' once updatePosition measures the menu;
            // an `initial`-only origin would never update after that
            // flip, and the menu would still scale in from the top
            // edge instead of the bottom when opening upward.
            style={
              portal && portalPos
                ? ({
                    position: positionStrategy,
                    top: portalPos.top,
                    left: portalPos.left,
                    minWidth: portalPos.minWidth,
                    zIndex: portalZIndex,
                    transformOrigin: `${verticalPosition === 'top' ? 'bottom' : 'top'} ${menuPosition}`,
                    willChange: 'transform, opacity',
                  } as React.CSSProperties)
                : ({
                    transformOrigin: `${verticalPosition === 'top' ? 'bottom' : 'top'} ${menuPosition}`,
                    willChange: 'transform, opacity',
                  } as React.CSSProperties)
            }
            onClick={(event) => {
              if (!closeOnSelect) return;
              const target = event.target as HTMLElement | null;
              if (!target) return;
              if (target.closest('li.dropdown-item')) {
                setIsOpen(false);
                // restore focus to trigger
                const btn = containerRef.current?.querySelector(
                  'button',
                ) as HTMLButtonElement | null;
                btn?.focus();
              }
            }}
          >
            <div className="dropdown-button__content">{dropdownContent}</div>
          </motion.ul>
        );

        if (portal && mounted) {
          const container = (
            typeof portalContainer === 'function'
              ? (portalContainer() ?? document.body)
              : (portalContainer ?? document.body)
          ) as HTMLElement;
          return createPortal(<AnimatePresence>{isOpen && menu}</AnimatePresence>, container);
        }
        return <AnimatePresence>{isOpen && menu}</AnimatePresence>;
      })()}
    </div>
  );
});
